import { startOfDay } from 'date-fns';
import { useState } from 'react';
import { LuShield } from 'react-icons/lu';

import { useRunAllocation } from '@/cip/api/optimization';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency, formatDate, formatPercent, useRequiredParams } from 'utils';
import { useListCashflowConfigs } from 'views/portfolio/account-group-queries';
import { frequencyTypeToLabel } from 'views/portfolio/PortfolioPage/utils/frequencyToLabel';

import { getPolicyWithdrawals } from './investment-policy-overview';
import { summarizePolicySecuredTargets } from './policy-securedness';
import {
    incompleteInflationNote,
    totalPolicyWithdrawals,
    upcomingPolicyWithdrawals,
    withdrawalTitle,
} from './policy-withdrawals';
import { PolicyQueryError } from './PolicyQueryError';
import {
    PolicyTable as Table,
    PolicyTableBody as TableBody,
    PolicyTableCell as TableCell,
    PolicyTableHead as TableHead,
    PolicyTableHeader as TableHeader,
    PolicyTableRow as TableRow,
} from './PolicyTable';
import { usePolicyScope } from './usePolicyScope';

const periods = [
    { years: 1, label: 'Next 12 Months' },
    { years: 3, label: 'Next 3 Years' },
    { years: 5, label: 'Next 5 Years' },
    { years: 10, label: 'Next 10 Years' },
];

export const PolicyWithdrawals = () => {
    const { accountGUID, accountGroupGUID } = useRequiredParams(['accountGUID', 'accountGroupGUID']);
    const [years, setYears] = useState('1');
    const scope = usePolicyScope(accountGUID, accountGroupGUID);
    const configsQuery = useListCashflowConfigs(accountGroupGUID, undefined, { throwOnError: false });
    const allocationQuery = useRunAllocation(accountGroupGUID, { throwOnError: false });
    const isError = scope.status === 'error' || configsQuery.isLoadingError;
    const ready = scope.status === 'ready' && !configsQuery.isPending && !configsQuery.isLoadingError;
    const { accounts } = scope;
    const today = startOfDay(new Date());
    const withdrawals = ready
        ? upcomingPolicyWithdrawals(
              getPolicyWithdrawals(configsQuery.data ?? [], accounts, scope.profile.accountGroup),
              today,
          )
        : [];
    const displayTotal = (periodYears: number) => {
        if (!ready) return '—';
        const amount = totalPolicyWithdrawals(withdrawals, periodYears, today);
        if (Number.isFinite(amount)) return formatCurrency(amount);
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span tabIndex={0} className="cursor-help underline decoration-dotted underline-offset-4">
                        Unavailable
                    </span>
                </TooltipTrigger>
                <TooltipContent>{incompleteInflationNote(withdrawals)}</TooltipContent>
            </Tooltip>
        );
    };
    const securedTargets = summarizePolicySecuredTargets(
        ready ? allocationQuery.data : undefined,
        accounts.map((item) => item.accountGUID),
    );
    const securedFallback = ready && allocationQuery.isPending ? 'Loading…' : 'Unavailable';
    const securedPercentage =
        securedTargets.percentage === 'mixed'
            ? 'Varies by account'
            : securedTargets.percentage === undefined
              ? securedFallback
              : formatPercent(securedTargets.percentage * 100, 1);
    const protectionKnown = ready && accounts.every((item) => Number.isFinite(Number(item.initialSecuredness)));
    const protectionOn = accounts.some((item) => Number(item.initialSecuredness) > 0);

    return (
        <div className="space-y-4.5">
            <div className="grid items-center gap-4 xl:min-h-14 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                <h2 id="policy-withdrawal-title" className="text-cocoa-1000 text-[22px] font-medium">
                    Withdrawal Plan
                </h2>
                <dl className="flex flex-wrap items-start gap-8 text-center xl:col-start-2">
                    <div title="Calculated target securedness, not current holdings. Different account targets are not averaged.">
                        <dt className="text-cocoa-500 text-[13px]">% Secured</dt>
                        <dd className="text-cocoa-1000 mt-1 text-lg font-medium tabular-nums">{securedPercentage}</dd>
                    </div>
                    <div title="Calculated target allocation to the secured bond sleeve, not current holdings.">
                        <dt className="text-cocoa-500 text-[13px]">$ Secured</dt>
                        <dd className="text-cocoa-1000 mt-1 text-lg font-medium tabular-nums">
                            {securedTargets.amount === undefined
                                ? securedFallback
                                : formatCurrency(securedTargets.amount)}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-cocoa-500 text-[13px]">Next 12 Months</dt>
                        <dd className="text-cocoa-1000 mt-1 text-lg font-medium tabular-nums">{displayTotal(1)}</dd>
                    </div>
                </dl>
            </div>
            <Card
                aria-labelledby="withdrawal-lifestyle-protection-title"
                className="flex flex-row items-center gap-3.5 rounded-[10px] border-stone-200 px-6 py-[22px] shadow-none"
            >
                <div className="text-cocoa-500 flex size-10 shrink-0 items-center justify-center rounded-lg bg-stone-100">
                    <LuShield aria-hidden="true" className="size-[22px]" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 id="withdrawal-lifestyle-protection-title" className="text-cocoa-1000 text-[15px] font-medium">
                        Lifestyle protection
                    </h3>
                    <p className="text-cocoa-500 mt-1 text-sm">
                        Hold upcoming withdrawals in short-term treasuries so they are secured from market swings.
                    </p>
                </div>
                <span className="text-cocoa-700 shrink-0 rounded-[5px] bg-stone-100 px-2 py-1 text-xs font-medium">
                    {protectionKnown ? (protectionOn ? 'On' : 'Off') : '—'}
                </span>
            </Card>
            {allocationQuery.isLoadingError && (
                <PolicyQueryError message="Couldn’t load lifestyle protection targets." queries={[allocationQuery]} />
            )}
            {isError ? (
                <PolicyQueryError
                    message="Couldn’t load withdrawal plan."
                    queries={[...scope.retryQueries, configsQuery]}
                />
            ) : (
                <Table
                    className="min-w-[720px] table-fixed"
                    footer={
                        <>
                            <div className="flex flex-wrap items-center gap-3">
                                <label htmlFor="withdrawal-total-period" className="text-cocoa-700 text-sm">
                                    Total projected withdrawals over
                                </label>
                                <Select value={years} onValueChange={setYears}>
                                    <SelectTrigger
                                        id="withdrawal-total-period"
                                        className="h-8 w-44 border-0 bg-transparent px-2 font-medium shadow-none"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {periods.map((period) => (
                                            <SelectItem key={period.years} value={String(period.years)}>
                                                {period.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <span className="text-cocoa-1000 font-medium tabular-nums" aria-live="polite">
                                {displayTotal(Number(years))}
                            </span>
                        </>
                    }
                >
                    <colgroup>
                        <col className="w-[30%]" />
                        <col className="w-[18%]" />
                        <col className="w-[18%]" />
                        <col className="w-[20%]" />
                        <col className="w-[14%]" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            {['Purpose', 'Starts', 'Frequency', 'From account', 'Amount'].map((label) => (
                                <TableHead key={label} className={label === 'Amount' ? 'text-right' : undefined}>
                                    {label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!ready || withdrawals.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-cocoa-500 py-8 text-center" role="status">
                                    {ready ? 'No withdrawals scheduled.' : 'Loading withdrawal plan…'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            withdrawals.map((config) => (
                                <TableRow key={config.cashFlowConfigGUID}>
                                    <TableCell>
                                        <div className="truncate font-medium" title={withdrawalTitle(config)}>
                                            {withdrawalTitle(config)}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-cocoa-800 whitespace-nowrap">
                                        {formatDate(config.factorDateFrame.startDate, 'MMM d, yyyy')}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {frequencyTypeToLabel(config.factorDateFrame.frequencyType)}
                                    </TableCell>
                                    <TableCell>
                                        {config.accountGUID
                                            ? (accounts.find((item) => item.accountGUID === config.accountGUID)
                                                  ?.accountName.plainText ?? 'Unknown account')
                                            : 'Any Account'}
                                    </TableCell>
                                    <TableCell className="text-right font-medium tabular-nums">
                                        {formatCurrency(config.amount)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
            <p className="text-cocoa-500 text-xs">
                Projected net withdrawals, including configured inflation adjustments. Amounts shown per occurrence
                above are the base amounts. Secured values are calculated allocation targets, not current holdings.
            </p>
        </div>
    );
};
